import { useEffect } from 'react';
import {
  BOARD_INSTANCE_GROUP_TYPE,
  BOARD_INSTANCE_GROUP_VISIBLE_UNITS_LIMIT,
  VALUE_CHAIN_MAP,
} from 'constants/general';
import {
  CONNECTION_TYPES,
  VALUE_CHAIN_CONNECTION_TYPES,
  VALUE_CHAIN_MAP_CUSTOM_NODES,
  VALUE_CHAIN_UNIT_TYPES,
} from 'constants/interfaces';

import { getCountryDisplayName } from '../../../../../../utils/country';
import {
  getUnitBusinessUnitType,
  getUnitEstimated,
  getUnitHeight,
} from '../../../../../../utils/reactFlowHelpers';

import type { Edge, Node } from '@xyflow/react';
import type { TValueChainUnitType } from '../../../../../../constants/interfaces';
import type {
  IBoardInstanceGroup,
  TBoardInstanceGroupType,
} from '../../../../../../store/slices/valueChainMap/interfaces/IBoardInstanceGroup';
import type { IBoardUnit } from '../../../../../../store/slices/valueChainMap/interfaces/IBoardUnit';
import type { TCountryLookup } from '../../../../../../utils/country';
import type { IUseValueChainGroupNodesParams } from '../interfaces/IUseValueChainGroupNodes';

// Builds board / swimlane / group / card nodes for the Location & Business
// Structure grouping modes. Owns setNodes/setEdges while grouping is active.
const useValueChainGroupNodes = ({
  isActive,
  boards,
  groupsByBoardId,
  unitsByBoardId,
  expandedGroupIds,
  showAllGroupIds,
  groupByType,
  countryLookup,
  profitCenterNameById,
  onToggleGroup,
  onShowMoreGroupUnits,
  setNodes,
  setEdges,
}: IUseValueChainGroupNodesParams) => {
  useEffect(() => {
    if (!isActive) {
      return;
    }

    const noop = () => {};

    const MIN_GROUPED_BOARD_HEIGHT = VALUE_CHAIN_MAP.TITLE_HEIGHT
      + VALUE_CHAIN_MAP.BOARD_PADDING
      + 800;

    const getDominantCardType = (units: IBoardUnit[]): TValueChainUnitType | undefined => {
      if (units.length === 0) {
        return undefined;
      }

      const countByType = new Map<TValueChainUnitType, number>();
      units.forEach((unit) => {
        countByType.set(unit.type, (countByType.get(unit.type) || 0) + 1);
      });

      let dominantType = units[0].type;
      let dominantCount = 0;
      units.forEach((unit) => {
        const typeCount = countByType.get(unit.type) || 0;
        if (typeCount > dominantCount) {
          dominantCount = typeCount;
          dominantType = unit.type;
        }
      });

      return dominantType;
    };

    const resolveGroupTitle = (
      group: IBoardInstanceGroup,
      resolveGroupByType: TBoardInstanceGroupType,
      resolveCountryLookup: TCountryLookup,
      resolveProfitCenterNameById: Map<string, string>,
    ): string => {
      if (resolveGroupByType === BOARD_INSTANCE_GROUP_TYPE.ORGANIZATIONAL) {
        const { profitCenterUID } = group.typeData;
        return (profitCenterUID && resolveProfitCenterNameById.get(profitCenterUID))
          || profitCenterUID
          || '';
      }

      return getCountryDisplayName(group.typeData.countryCode, resolveCountryLookup)
        || group.typeData.countryCode
        || '';
    };

    const nextNodes: Node[] = [];
    // Rendered group instances, keyed by `${groupId}_${columnId}` — the pair that
    // uniquely identifies a unit record's placement.
    const renderedGroupKeys = new Set<string>();
    // The same unit can belong to several groups, and even appear several times
    // within one group (one IBoardUnit record per membership), so node ids are
    // made unique per record — React Flow silently drops duplicate ids, leaving
    // an empty gap where the card should be. A record is present in this map
    // only when its card is actually rendered (expanded group, within the
    // visible "show more" slice); connection endpoints attach to that card and
    // otherwise fall back to the group card.
    const unitNodeIdByRecord = new Map<IBoardUnit, string>();
    // Match the group pill / show-more pill to the child card width so all three
    // share the same UNIT_HORIZONTAL_PADDING on both sides and read as centered.
    const groupNodeWidth = VALUE_CHAIN_MAP.SWIMLANE_WIDTH - VALUE_CHAIN_MAP.UNIT_HORIZONTAL_PADDING * 2;
    const groupStartY = VALUE_CHAIN_MAP.TITLE_HEIGHT + VALUE_CHAIN_MAP.UNIT_TOP_PADDING;
    let accumulatedBoardX = 0;
    let maxBoardHeight = VALUE_CHAIN_MAP.TITLE_HEIGHT * 2;

    boards.forEach((board) => {
      const sortedColumns = [...board.columns].sort((a, b) => a.orderIndex - b.orderIndex);
      const additionalSpace = sortedColumns.length > 1
        ? (sortedColumns.length - 1) * VALUE_CHAIN_MAP.SWIMLANE_GAP
        : 0;
      const boardWidth = VALUE_CHAIN_MAP.BOARD_PADDING * 2
        + sortedColumns.length * VALUE_CHAIN_MAP.SWIMLANE_WIDTH
        + additionalSpace;
      const boardStartX = accumulatedBoardX;

      nextNodes.push({
        id: `board_${board.id}`,
        type: VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD,
        draggable: false,
        focusable: false,
        selectable: false,
        style: { zIndex: 0 },
        data: { id: board.id, name: board.name, boardWidth },
        position: { x: boardStartX, y: 0 },
      });

      accumulatedBoardX += boardWidth + VALUE_CHAIN_MAP.BOARD_GAP;

      const boardGroups = groupsByBoardId[board.id] || [];
      const boardUnits = unitsByBoardId[board.id] || [];

      sortedColumns.forEach((column, columnIndex) => {
        const swimlaneX = boardStartX + VALUE_CHAIN_MAP.BOARD_PADDING
          + columnIndex * (VALUE_CHAIN_MAP.SWIMLANE_WIDTH + VALUE_CHAIN_MAP.SWIMLANE_GAP);

        nextNodes.push({
          id: `swimlane_${column.id}`,
          type: VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE,
          draggable: false,
          selectable: false,
          style: { zIndex: 1 },
          data: {
            id: column.id,
            name: column.name,
            type: column.type,
            onEditSwimlane: noop,
            onDeleteSwimlane: noop,
            isAccessDisabled: true,
          },
          position: { x: swimlaneX, y: VALUE_CHAIN_MAP.TITLE_HEIGHT },
        });

        const columnGroups = boardGroups
          .filter((group) => group.columnId === column.id)
          .sort((a, b) => resolveGroupTitle(a, groupByType, countryLookup, profitCenterNameById)
            .localeCompare(
              resolveGroupTitle(b, groupByType, countryLookup, profitCenterNameById),
              undefined,
              { sensitivity: 'base' },
            ));

        let localY = groupStartY;

        columnGroups.forEach((group) => {
          const isExpanded = expandedGroupIds.has(group.id);
          // A unit record belongs to a group only when BOTH its groupId and its
          // columnId match: the same unit can have records in other columns, and
          // matching by groupId alone pulls those foreign records into the group.
          const groupUnits = boardUnits.filter(
            (unit) => unit.groupId === group.id
              && unit.columnId === column.id
              && unit.type !== VALUE_CHAIN_UNIT_TYPES.PROFIT_CENTER,
          );

          if (groupUnits.length === 0) {
            return;
          }

          nextNodes.push({
            id: `group_${group.id}`,
            type: VALUE_CHAIN_MAP_CUSTOM_NODES.GROUP,
            parentId: `swimlane_${column.id}`,
            draggable: false,
            connectable: false,
            selectable: true,
            style: { zIndex: 2, width: groupNodeWidth },
            data: {
              groupId: group.id,
              columnId: column.id,
              boardId: board.id,
              title: resolveGroupTitle(group, groupByType, countryLookup, profitCenterNameById),
              count: groupUnits.length,
              cardType: getDominantCardType(groupUnits),
              isExpanded,
              groupType: groupByType,
              onToggleGroup,
            },
            position: { x: VALUE_CHAIN_MAP.UNIT_HORIZONTAL_PADDING, y: localY },
          });

          renderedGroupKeys.add(`${group.id}_${column.id}`);

          localY += VALUE_CHAIN_MAP.BOARD_INSTANCE_GROUP_HEADER_HEIGHT;

          if (isExpanded) {
            localY += VALUE_CHAIN_MAP.BOARD_INSTANCE_GROUP_HINT_HEIGHT
              + VALUE_CHAIN_MAP.BOARD_INSTANCE_GROUP_GAP;

            const sortedUnits = groupUnits
              .slice()
              .sort((a, b) => a.orderIndex - b.orderIndex);
            const isShowingAllUnits = showAllGroupIds.has(group.id);
            const visibleUnits = isShowingAllUnits
              ? sortedUnits
              : sortedUnits.slice(0, BOARD_INSTANCE_GROUP_VISIBLE_UNITS_LIMIT);
            const hiddenUnitsCount = sortedUnits.length - visibleUnits.length;

            visibleUnits.forEach((unit, unitIndex) => {
              const unitNodeId = `unit_${unit.unitId}_group_${group.id}_item_${unitIndex}`;
              unitNodeIdByRecord.set(unit, unitNodeId);

              nextNodes.push({
                id: unitNodeId,
                type: VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT,
                parentId: `swimlane_${column.id}`,
                draggable: false,
                connectable: false,
                selectable: true,
                style: { zIndex: 2 },
                data: {
                  ...unit,
                  swimlaneId: `swimlane_${column.id}`,
                  childrenCount: 0,
                  totalChildrenHeight: 0,
                  depth: 0,
                  isCollapsed: false,
                  isDraftReviewMode: false,
                },
                position: { x: VALUE_CHAIN_MAP.UNIT_HORIZONTAL_PADDING, y: localY },
              });

              const cardHeight = getUnitHeight(
                unit.type,
                getUnitBusinessUnitType(unit),
                getUnitEstimated(unit),
              );
              localY += cardHeight + VALUE_CHAIN_MAP.UNIT_VERTICAL_GAP;
            });

            if (hiddenUnitsCount > 0) {
              nextNodes.push({
                id: `group_more_${group.id}`,
                type: VALUE_CHAIN_MAP_CUSTOM_NODES.GROUP_MORE,
                parentId: `swimlane_${column.id}`,
                draggable: false,
                connectable: false,
                selectable: true,
                style: { zIndex: 2, width: groupNodeWidth },
                data: {
                  groupId: group.id,
                  hiddenCount: hiddenUnitsCount,
                  onShowMore: onShowMoreGroupUnits,
                },
                position: { x: VALUE_CHAIN_MAP.UNIT_HORIZONTAL_PADDING, y: localY },
              });

              localY += VALUE_CHAIN_MAP.BOARD_INSTANCE_GROUP_MORE_BUTTON_HEIGHT
                + VALUE_CHAIN_MAP.UNIT_VERTICAL_GAP;
            }
          }

          localY += VALUE_CHAIN_MAP.BOARD_INSTANCE_GROUP_GAP;
        });

        maxBoardHeight = Math.max(
          maxBoardHeight,
          VALUE_CHAIN_MAP.TITLE_HEIGHT + localY + VALUE_CHAIN_MAP.BOARD_PADDING,
        );
      });
    });

    const accumulatedMaxBoardHeight = Math.max(maxBoardHeight, MIN_GROUPED_BOARD_HEIGHT);
    const nodesWithHeight = nextNodes.map((node) => {
      if (
        node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD
        && node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE
      ) {
        return node;
      }

      return {
        ...node,
        data: { ...node.data, accumulatedMaxBoardHeight },
      };
    });

    // Build connection edges from each unit's own connections
    // (lConToUnits / sConToUnits / gsConToUnits / mConToUnits). Each endpoint is resolved
    // independently: when a unit's group is expanded and the unit itself is
    // rendered, the edge attaches to the unit card; otherwise (group collapsed,
    // or the unit hidden behind "show more") it attaches to the group card.
    // Multiple underlying connections that resolve to the same endpoint pair and
    // type are aggregated into a single edge. Multiple types on the same pair are
    // fanned out with small vertical offsets so their arrow markers do not overlap.
    const connectionTypeOrder: readonly string[] = [
      VALUE_CHAIN_CONNECTION_TYPES.LOGICAL,
      VALUE_CHAIN_CONNECTION_TYPES.SERVICE,
      VALUE_CHAIN_CONNECTION_TYPES.SUPPLIER_OF_GOODS,
      VALUE_CHAIN_CONNECTION_TYPES.MANUFACTURE,
    ];

    // A unit that belongs to several groups has one record per group; keep them
    // all so a connection can attach to every rendered instance of the unit.
    const unitRecordsByUnitId = new Map<number, IBoardUnit[]>();
    Object.values(unitsByBoardId).forEach((units) => {
      units.forEach((unit) => {
        const unitRecords = unitRecordsByUnitId.get(unit.unitId) || [];
        unitRecords.push(unit);
        unitRecordsByUnitId.set(unit.unitId, unitRecords);
      });
    });

    const resolveConnectionEndpoint = (unit: IBoardUnit): string | null => {
      if (!renderedGroupKeys.has(`${unit.groupId}_${unit.columnId}`)) {
        return null;
      }

      return unitNodeIdByRecord.get(unit) || `group_${unit.groupId}`;
    };

    const groupEdgeSpecsById = new Map<string, {
      id: string,
      source: string,
      target: string,
      connectionType: string,
      count: number,
    }>();

    Object.values(unitsByBoardId).forEach((units) => {
      units.forEach((sourceUnit) => {
        const source = resolveConnectionEndpoint(sourceUnit);
        if (!source) {
          return;
        }

        Object.entries(CONNECTION_TYPES).forEach(([connectionField, connectionType]) => {
          const connections = sourceUnit[connectionField as keyof typeof CONNECTION_TYPES] as
            { toId: number }[] | undefined;

          (connections ?? []).forEach((connection) => {
            const targetUnits = unitRecordsByUnitId.get(connection.toId) || [];

            targetUnits.forEach((targetUnit) => {
              const target = resolveConnectionEndpoint(targetUnit);
              if (!target || source === target) {
                return;
              }

              const edgeId = `group-conn-${source}-${target}-${connectionType}`;
              const existingSpec = groupEdgeSpecsById.get(edgeId);
              if (existingSpec) {
                existingSpec.count += 1;
                return;
              }

              groupEdgeSpecsById.set(edgeId, {
                id: edgeId,
                source,
                target,
                connectionType,
                count: 1,
              });
            });
          });
        });
      });
    });

    const groupEdgeSpecsByPair = new Map<string, {
      id: string,
      source: string,
      target: string,
      connectionType: string,
      count: number,
    }[]>();

    groupEdgeSpecsById.forEach((spec) => {
      const pairKey = `${spec.source}-${spec.target}`;
      const pairSpecs = groupEdgeSpecsByPair.get(pairKey) || [];
      pairSpecs.push(spec);
      groupEdgeSpecsByPair.set(pairKey, pairSpecs);
    });

    const GROUP_EDGE_TYPE_GAP = 8;
    const nextEdges: Edge[] = [];

    groupEdgeSpecsByPair.forEach((pairSpecs) => {
      const presentTypes = connectionTypeOrder.filter(
        (connectionType) => pairSpecs.some((spec) => spec.connectionType === connectionType),
      );

      pairSpecs.forEach((spec) => {
        const typeIndex = presentTypes.indexOf(spec.connectionType);
        const offset = presentTypes.length > 1
          ? (typeIndex - (presentTypes.length - 1) / 2) * GROUP_EDGE_TYPE_GAP
          : 0;

        nextEdges.push({
          id: spec.id,
          type: 'custom',
          source: spec.source,
          target: spec.target,
          data: {
            connectionType: spec.connectionType,
            connection: {},
            count: spec.count,
            sourceOffset: offset,
            targetOffset: offset,
            pathOffset: offset * 1.5,
          },
        });
      });
    });

    setNodes(nodesWithHeight);
    setEdges(nextEdges);
  }, [
    isActive,
    boards,
    groupsByBoardId,
    unitsByBoardId,
    expandedGroupIds,
    showAllGroupIds,
    groupByType,
    countryLookup,
    profitCenterNameById,
    onToggleGroup,
    onShowMoreGroupUnits,
    setNodes,
    setEdges,
  ]);
};

export default useValueChainGroupNodes;
